import mixpanel from 'mixpanel-browser';
import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'src/state/auth';
import { MPEvents, MpEvent } from './types';
import { getLocationParam } from './util';
import { isDev } from 'src/config';

const MIXPANEL_TOKEN = process.env.MIXPANEL_TOKEN || 'null';
const isEnvReady = MIXPANEL_TOKEN !== 'null' && !isDev;

const useMixpanel = () => {
  const [isEnabled, setIsEnabled] = useState(false);
  const { profile, features } = useAuth();
  const location = useLocation();

  const isInitializedRef = useRef(false);
  const identifiedUserIdRef = useRef<string | null>(null);
  const lastOrgIdRef = useRef<string | undefined>(undefined);
  const lastWorkspaceIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    setIsEnabled(features['app.mixpanel'] && isEnvReady);
  }, [features]);

  useEffect(() => {
    if (isEnabled && !isInitializedRef.current) {
      mixpanel.init(MIXPANEL_TOKEN, {
        debug: process.env.NODE_ENV === 'development',
        persistence: 'localStorage',
        autotrack: true,
        ignore_dnt: true,
        track_pageview: false,
        api_host: 'https://api-eu.mixpanel.com',
      });
      isInitializedRef.current = true;
    }
  }, [isEnabled]);

  useEffect(() => {
    if (isEnabled && isInitializedRef.current) {
      const extraParams = getLocationParam(location.pathname);
      mixpanel.track(MPEvents.PageView, {
        path: location.pathname,
        location: extraParams.location,
        tags: ['FE', ...extraParams.tags],
      });
    }
  }, [location.pathname, isEnabled]);

  useEffect(() => {
    if (isEnabled && isInitializedRef.current) {
      if (profile?.user) {
        const userId = String(profile.user.id);

        if (identifiedUserIdRef.current !== userId) {
          mixpanel.identify(userId);
          identifiedUserIdRef.current = userId;

          mixpanel.people.set({
            organizationID: profile?.activeOrganisation?.id,
            workspaceID: profile?.activeWorkspace?.id,
          });

          mixpanel.register({
            organizationID: profile?.activeOrganisation?.id,
            workspaceID: profile?.activeWorkspace?.id,
          });

          lastOrgIdRef.current = profile?.activeOrganisation?.id;
          lastWorkspaceIdRef.current = profile?.activeWorkspace?.id;
        }
      } else if (identifiedUserIdRef.current !== null) {
        mixpanel.reset();
        identifiedUserIdRef.current = null;
        lastOrgIdRef.current = undefined;
        lastWorkspaceIdRef.current = undefined;
      }
    }
  }, [profile?.user?.id, isEnabled]);

  useEffect(() => {
    if (isEnabled && isInitializedRef.current && identifiedUserIdRef.current) {
      const currentOrgId = profile?.activeOrganisation?.id;
      const currentWorkspaceId = profile?.activeWorkspace?.id;

      if (lastOrgIdRef.current !== currentOrgId || lastWorkspaceIdRef.current !== currentWorkspaceId) {
        mixpanel.register({
          organizationID: currentOrgId,
          workspaceID: currentWorkspaceId,
        });

        mixpanel.people.set({
          organizationID: currentOrgId,
          workspaceID: currentWorkspaceId,
        });

        lastOrgIdRef.current = currentOrgId;
        lastWorkspaceIdRef.current = currentWorkspaceId;
      }
    }
  }, [profile?.activeOrganisation?.id, profile?.activeWorkspace?.id, isEnabled]);
};

export const mpEvent: MpEvent = (event, params) => {
  if (!isEnvReady) return;

  let tags = params?.tags || [];

  if (event === MPEvents.Link || event === MPEvents.PageView) {
    tags = ['NAVIGATION', ...tags];
  }
  if (event === MPEvents.ButtonClick) {
    tags = ['BUTTON', ...tags];
  }
  if (event === MPEvents.GraphLegendToggle || event === MPEvents.GraphScaleSwitch) {
    tags = ['CHART', ...tags];
  }
  if (event === MPEvents.VariableAddedToFormula || event === MPEvents.FormulaVariablesOpen) {
    tags = ['VARIABLES', ...tags];
  }
  if (
    event === MPEvents.WizardSlideChanged ||
    event === MPEvents.WizardOpened ||
    event === MPEvents.WizardClosed ||
    event === MPEvents.WizardSubmit
  ) {
    tags = ['WIZARD', ...tags];
  }

  if (
    event === MPEvents.TableColumnsChange ||
    event === MPEvents.TableSortChange ||
    event === MPEvents.TableSearch ||
    event === MPEvents.TableRowExpand ||
    event === MPEvents.TableCheckboxClick
  ) {
    tags = ['TABLE', ...tags];
  }
  if (event === MPEvents.DateToTimelineLink) {
    tags = ['TIMELINE', ...tags];
  }

  tags.push('FE');

  try {
    mixpanel.track(event, { ...params, $tags: tags });
  } catch (error) {}
};

export default useMixpanel;
